import React, { useEffect, useRef, useState } from "react";
import { SalesActivityTypeCount } from "./activitiesReportService";
import { getCardAccent, resolveActivityIcon } from "./activityChartPalette";

interface ActivityScoreCardsProps {
  types: SalesActivityTypeCount[];
  loading: boolean;
}

const TOTAL_CARD_ACCENT = { base: "#4b5a71", dark: "#2b3646" };

const ActivityScoreCards: React.FC<ActivityScoreCardsProps> = ({
  types,
  loading,
}) => {
  const stripRef = useRef<HTMLDivElement>(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const updateScrollState = () => {
    const el = stripRef.current;
    if (!el) return;
    setCanScrollPrev(el.scrollLeft > 4);
    setCanScrollNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    // Centering an overflowing flex row makes the browser start the scroll
    // position mid-content instead of at the first card, so only center
    // the cards when they all fit without scrolling.
    setIsOverflowing(el.scrollWidth > el.clientWidth + 1);
  };

  useEffect(() => {
    updateScrollState();
    const el = stripRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [types.length, loading]);

  const scrollByPage = (direction: 1 | -1) => {
    const el = stripRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.85, behavior: "smooth" });
  };

  const total = types.reduce((sum, type) => sum + (type.counts || 0), 0);

  if (loading) {
    return (
      <div className="ar-kpi-carousel">
        <div className="ar-kpi-strip">
          {[0, 1, 2, 3, 4].map((key) => (
            <div className="ar-kpi-card" key={key}>
              <div className="ar-skeleton mb-3" style={{ width: 44, height: 44, borderRadius: 13 }} />
              <div className="ar-skeleton mb-2" style={{ width: "60%", height: 12 }} />
              <div className="ar-skeleton" style={{ width: "40%", height: 24 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="ar-kpi-carousel">
      <button
        type="button"
        className="ar-kpi-nav ar-kpi-nav-prev"
        onClick={() => scrollByPage(-1)}
        disabled={!canScrollPrev}
        aria-label="Show previous activity types"
      >
        <i className="ti ti-chevron-left" />
      </button>

      <div
        className={`ar-kpi-strip ${!isOverflowing ? "ar-kpi-strip--centered" : ""}`}
        ref={stripRef}
      >
        <div
          className="ar-kpi-card"
          style={
            {
              "--ar-accent": TOTAL_CARD_ACCENT.base,
              "--ar-accent-dark": TOTAL_CARD_ACCENT.dark,
            } as React.CSSProperties
          }
        >
          <span className="ar-kpi-glow" />
          <div className="ar-kpi-head d-flex align-items-start justify-content-between mb-3">
            <p className="ar-kpi-label mb-0">Total Activities</p>
            <span className="ar-kpi-icon">
              <i className="ti ti-chart-bar" />
            </span>
          </div>
          <h2 className="ar-kpi-value">{total}</h2>
          <p className="ar-kpi-sub mb-0">
            across {types.length} activity {types.length === 1 ? "type" : "types"}
          </p>
        </div>

        {types.map((type, index) => {
          const accent = getCardAccent(index);
          const icon = resolveActivityIcon(
            type.icon,
            type.crm_sales_activity_type_display_name
          );
          const share = total > 0 ? Math.round((type.counts / total) * 100) : 0;
          return (
            <div
              className="ar-kpi-card"
              key={type.crm_sales_activity_type_id}
              style={
                {
                  "--ar-accent": accent.base,
                  "--ar-accent-dark": accent.dark,
                } as React.CSSProperties
              }
            >
              <span className="ar-kpi-glow" />
              <div className="ar-kpi-head d-flex align-items-start justify-content-between mb-3">
                <p className="ar-kpi-label mb-0" title={type.crm_sales_activity_type_display_name}>
                  {type.crm_sales_activity_type_display_name}
                </p>
                <span className="ar-kpi-icon">
                  <i className={icon} />
                </span>
              </div>
              <h2 className="ar-kpi-value">{type.counts}</h2>
              <div className="ar-kpi-progress-track">
                <div className="ar-kpi-progress-fill" style={{ width: `${share}%` }} />
              </div>
              <p className="ar-kpi-sub mb-0">{share}% of total activities</p>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        className="ar-kpi-nav ar-kpi-nav-next"
        onClick={() => scrollByPage(1)}
        disabled={!canScrollNext}
        aria-label="Show more activity types"
      >
        <i className="ti ti-chevron-right" />
      </button>
    </div>
  );
};

export default ActivityScoreCards;
